import { useCallback, useMemo } from 'react';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import CustomLink from '@/components/general/CustomLink';
import CustomTable from '@/components/general/CustomTable';
import CustomTableCell from '@/components/general/CustomTableCell';
import CustomTableRow from '@/components/general/CustomTableRow';
import { getEndpointWithParams } from '@/routes/config';
import { CheckinButtonLabel, HeaderTable } from '@/types';
import { CheckinSummary } from '@/types/api';
import { Paths } from '@/types/routes';
import { defaultCheckinDateFormat } from '@/utils/checkins';

type TableType = {
  id: string;
  userName: string;
  userId: string;
  dueDate: Date | string;
  submittedDate: Date | string;
  isAnsweredCheckin: boolean;
};

const TableOfCheckins = function ({ checkins }: { checkins: CheckinSummary[] }): JSX.Element {
  const tableHeaders: HeaderTable<TableType>[] = useMemo(
    () => [
      { id: 'userName', label: 'User Name  ' },
      { id: 'dueDate', label: 'Due Date' },
      { id: 'submittedDate', label: 'Submitted Date' },
      null,
    ],
    []
  );

  const formattedDate = checkins.map((checkin) => ({
    userName: checkin.userName ?? '',
    id: checkin?.id,
    userId: checkin?.userId,
    dueDate: format(new Date(checkin?.dueDate), defaultCheckinDateFormat) ?? '',
    isAnsweredCheckin: !!checkin?.answeredAt,
    submittedDate: checkin?.answeredAt
      ? format(new Date(checkin?.answeredAt), defaultCheckinDateFormat)
      : 'Not submitted yet.',
  }));

  const renderCheckins = useCallback((row: TableType) => {
    const userCheckinsURL = getEndpointWithParams(Paths.USER_CHECKINS, { userId: row.userId });
    const userCheckinDetailsURL = getEndpointWithParams(Paths.USER_CHECKIN_DETAILS, {
      userId: row.userId,
      checkinId: row.id,
    });
    const buttonLabel = CheckinButtonLabel.NOT_REVIEWED;

    return (
      <CustomTableRow key={row.id}>
        <CustomTableCell data-testid="checkin-period">
          <CustomLink to={userCheckinsURL} data-testid="user-name-clickable">
            {row.userName}
          </CustomLink>
        </CustomTableCell>
        <CustomTableCell data-testid="checkin-submitted-date">
          {row.dueDate.toString()}
        </CustomTableCell>
        <CustomTableCell data-testid="checkin-submitted-date">
          {row.submittedDate.toString()}
        </CustomTableCell>
        <CustomTableCell align="right">
          <Button
            variant="outlined"
            component={RouterLink}
            to={userCheckinDetailsURL}
            disabled={!row.isAnsweredCheckin}
            data-testid="user-checkins-action-button"
          >
            {buttonLabel}
          </Button>
        </CustomTableCell>
      </CustomTableRow>
    );
  }, []);

  return (
    <CustomTable<TableType>
      data={formattedDate}
      rowRender={renderCheckins}
      initialOrderBy="userName"
      headersArray={tableHeaders}
      data-testid="user-checkins-list-table"
    />
  );
};

export default TableOfCheckins;
